import { useEffect, useState, useCallback, type KeyboardEvent } from 'react'
import { TextBox, type TextBoxProps } from './TextBox'
import { useDebounce } from '@/hooks/useDebounce'

export type FilterTextBoxProps = Omit<TextBoxProps, 'value' | 'setValue'> & {
  value?: string
  /** Called with the settled string after settleAfterMs of no typing */
  setValue?: (value: string) => void
  /** Milliseconds of no typing before the value is committed to the parent. Default: 500 */
  settleAfterMs?: number
}

export function FilterTextBox({
  value = '',
  setValue,
  placeholder = 'Search',
  settleAfterMs = 500,
  onKeyDown,
  ...rest
}: FilterTextBoxProps) {

  const [localValue, setLocalValue] = useState(() => (typeof value === 'string' ? value : ''))

  const settledValue = useDebounce(localValue, settleAfterMs)

  //--------------------------------------------------------------------------------
  // Sync from parent when value (e.g. external reset) changes
  useEffect(() => {
    setLocalValue(typeof value === 'string' ? value : '')
  }, [value])

  //--------------------------------------------------------------------------------
  // Commit settled value to parent when debounce catches up
  useEffect(() => {
    if (settledValue === localValue) {
      setValue?.(settledValue)
    }
  }, [settledValue, localValue, setValue])

  //--------------------------------------------------------------------------------
  const handleSetValue = useCallback((next: string | ((prev: string) => string)) => {
    setLocalValue(prev => typeof next === 'function' ? next(prev) : next)
  }, [])

  //--------------------------------------------------------------------------------
  const handleKeyDown = useCallback((e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      setValue?.(localValue)  // Fastrack with no delay
    } else if (e.key === 'Escape') {
      setLocalValue('')
    }
    onKeyDown?.(e)
  }, [localValue, setValue, onKeyDown])

  //--------------------------------------------------------------------------------
  return (
    <TextBox
      value={localValue}
      setValue={handleSetValue}
      placeholder={placeholder}
      onKeyDown={handleKeyDown}
      {...rest}
    />
  )
}
